import React, { useState, useMemo } from "react";
import { Bot, Loader2, LogOut, Timer, ShoppingCart, X, History, RotateCcw, ChevronDown, ChevronUp } from "lucide-react";
import { ThinkingBubble, AgentBubble, UserBubble, PricingProposalBubble } from "./ClientDashboard.Bubbles";
import { CreditsExhausted, RateLimitBar, ChatInput, useCountdown } from "./ClientDashboard.Controls";

function CountdownStrip({ offerExpiresAt, settings, account, siteSlug }) {
  const secsLeft = useCountdown(offerExpiresAt);
  const offerActive = secsLeft > 0 && !!offerExpiresAt;
  const price = settings?.offer_price;
  const currency = settings?.offer_currency || "UYU";
  const priceLabel = price ? `${currency} $${Number(price).toLocaleString("es-UY")}/mes` : "";
  const fmtSecs = (s) => {
    const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60); const r = s % 60;
    if (h > 0) return `${h}h ${m}m ${String(r).padStart(2, "0")}s`;
    return `${m}m ${String(r).padStart(2, "0")}s`;
  };
  if (!account?.is_trial && !offerActive) return null;
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-[#0A0A0A] to-[#1a1200] border-b border-[#ffc700]/20 flex-shrink-0">
      {offerActive ? (
        <>
          <Timer size={10} className="text-[#ffc700] flex-shrink-0" />
          <span className="text-[10px] font-black text-[#ffc700] tabular-nums">{fmtSecs(secsLeft)}</span>
          <span className="text-[9px] text-gray-500">— oferta bloqueada</span>
          {priceLabel && <span className="ml-auto text-[10px] font-black text-white">{priceLabel}</span>}
        </>
      ) : (
        <>
          <ShoppingCart size={10} className="text-gray-400 flex-shrink-0" />
          <span className="text-[10px] text-gray-400">La oferta por tiempo limitado expiró</span>
          {priceLabel && <span className="ml-auto text-[10px] font-semibold text-gray-400">{priceLabel}</span>}
        </>
      )}
    </div>
  );
}

function ChangeHistoryPanel({ checkpoints, onRestore, restoring }) {
  const [open, setOpen] = useState(false);
  if (!checkpoints || checkpoints.length === 0) return null;
  const recent = [...checkpoints].reverse().slice(0, 8);
  const fmtDate = (ts) => {
    if (!ts) return "";
    try { return new Date(ts).toLocaleDateString("es-UY", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); }
    catch { return ""; }
  };
  return (
    <div className="border-t border-gray-100 flex-shrink-0">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-2 text-[11px] text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors"
        data-testid="change-history-toggle"
      >
        <span className="flex items-center gap-1.5 font-semibold">
          <History size={11} /> Historial de cambios ({checkpoints.length})
        </span>
        {open ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
      </button>
      {open && (
        <div className="max-h-48 overflow-y-auto bg-gray-50 border-t border-gray-100">
          {recent.map((cp) => (
            <div key={cp.id || cp.number} className="flex items-start gap-2 px-4 py-2 border-b border-gray-100 last:border-0 hover:bg-gray-100 transition-colors group">
              <div className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-[9px] font-black mt-0.5">
                {cp.number}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] text-gray-700 font-medium leading-tight truncate">{cp.instruction || cp.summary}</p>
                <p className="text-[9px] text-gray-400 mt-0.5">{fmtDate(cp.timestamp)}</p>
              </div>
              <button
                onClick={() => onRestore(cp.number)}
                disabled={restoring}
                title={`Restaurar al estado antes del cambio #${cp.number}`}
                data-testid={`restore-checkpoint-${cp.number}`}
                className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 text-[10px] text-red-500 hover:text-red-700 font-semibold px-2 py-1 rounded-lg hover:bg-red-50 disabled:opacity-40"
              >
                {restoring ? <Loader2 size={9} className="animate-spin" /> : <RotateCcw size={9} />}
                Restaurar
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ChatPanel({
  account, localMods, previewHtml, setShowMobilePreview, closingChat, closeChat,
  messages, initials, acceptProposal, continueModifying, confirmApplied, restoreVersion,
  accepting, acceptingTimer, restoring, prevInstruction, isLoading, thinkingStage,
  messagesEndRef, rateLimitInfo, token, inputRef, input, setInput, sendMessage,
  attachedImage, setAttachedImage, pricingLoading, acceptPricing, declinePricing,
  isLimited, pendingProposal, offerExpiresAt, settings, siteSlug, onMinimize,
  checkpoints, previewLoading,
  siteImage, onSiteImageAttach, onSiteImageRemove,
  onDidntWork, onLoved, onStop, onSuggest,
  onApprovePlan, onRejectPlan,
}) {
  // Iter91 — memoize derived chat history so the .filter+.map+.reverse chain
  // doesn't re-run on every parent render. ChatInput uses this for ↑/↓ recall.
  const userHistory = useMemo(
    () => messages.filter(m => m.role === "user").map(m => m.content).reverse(),
    [messages]
  );

  return (
    <>
      {/* Chat header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 bg-white flex-shrink-0">
        <div className="flex items-center gap-2">
          <Bot size={14} className="text-[#6D28D9]" />
          <span className="text-xs font-bold text-gray-700">Asistente IA</span>
          <span className="text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
            {localMods?.total_available ?? "?"} créditos
          </span>
        </div>
        <div className="flex items-center gap-2">
          {previewHtml && (
            <button
              onClick={() => setShowMobilePreview(true)}
              className="md:hidden flex items-center gap-1 text-[10px] font-bold text-[#6D28D9] bg-purple-50 px-2 py-1 rounded-lg"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse inline-block" />
              Ver preview
            </button>
          )}
          <button
            data-testid="close-chat-btn"
            onClick={closeChat}
            disabled={closingChat || messages.length <= 2}
            className="flex items-center gap-1 text-[10px] text-gray-400 hover:text-gray-700 transition-colors disabled:opacity-40 px-2 py-1 rounded-lg hover:bg-gray-100"
            title="Cerrar sesión y guardar resumen"
          >
            {closingChat ? <Loader2 size={11} className="animate-spin" /> : <LogOut size={11} />}
            <span className="hidden sm:inline">Cerrar chat</span>
          </button>
          {/* X close (collapse to floating bubble) — placed last so it sits
              all the way to the right, with strong contrast on the white
              header so users find it instantly. */}
          {onMinimize && (
            <button
              data-testid="minimize-chat-btn"
              onClick={onMinimize}
              title="Cerrar panel (queda como burbuja flotante)"
              className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-900 text-white hover:bg-black hover:scale-105 active:scale-95 shadow-md transition-all"
            >
              <X size={16} strokeWidth={3} />
            </button>
          )}
        </div>
      </div>

      {/* Countdown strip — always visible for trial accounts */}
      {account?.is_trial && (
        <CountdownStrip
          offerExpiresAt={offerExpiresAt}
          settings={settings}
          account={account}
          siteSlug={siteSlug}
        />
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 pt-4 space-y-4 pb-2">
        {messages.map(msg => {
          if (msg.role === "user") return <UserBubble key={msg.id} msg={msg} initials={initials} />;
          if (msg.type === "pricing_proposal") return (
            <PricingProposalBubble key={msg.id} msg={msg}
              onAccept={acceptPricing} onDecline={declinePricing} loading={pricingLoading} />
          );
          return (
            <AgentBubble key={msg.id} msg={msg}
              onAccept={acceptProposal} onContinue={continueModifying}
              onConfirm={confirmApplied} onRestore={restoreVersion} onRestoreFlow={restoreVersion}
              accepting={accepting} acceptingTimer={acceptingTimer} restoring={restoring}
              prevInstruction={prevInstruction}
              previewLoading={previewLoading && msg.pending_id === pendingProposal?.pending_id}
              onDidntWork={onDidntWork} onLoved={onLoved}
              onApprovePlan={onApprovePlan} onRejectPlan={onRejectPlan} />
          );
        })}
        {isLoading && thinkingStage !== null && <ThinkingBubble stage={thinkingStage} />}
        <div ref={messagesEndRef} />
      </div>

      <RateLimitBar rl={rateLimitInfo} />
      {localMods?.is_locked && !isLoading && <CreditsExhausted token={token} />}

      {/* Change History Panel */}
      <ChangeHistoryPanel checkpoints={checkpoints} onRestore={restoreVersion} restoring={restoring} />

      {!localMods?.is_locked && (
        <div ref={inputRef}>
          <ChatInput
            value={input} onChange={setInput} onSend={sendMessage}
            disabled={isLoading} hasPending={!!pendingProposal} isLimited={isLimited}
            attachedImage={attachedImage} onImageAttach={setAttachedImage}
            onImageRemove={() => setAttachedImage(null)}
            siteImage={siteImage} onSiteImageAttach={onSiteImageAttach}
            onSiteImageRemove={onSiteImageRemove}
            history={userHistory}
            onStop={onStop}
            onSuggest={onSuggest}
          />
        </div>
      )}
    </>
  );
}
